import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import PageTitle from './components/PageTitle';
import DefaultLayout from './layout/DefaultLayout';
import AuthLayout from './layout/AuthLayout';
import AuthProvider, { useAuth } from './utils/Auth/AuthProvider';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { NonAuthRoute } from './routes/NonAuthRoute';
import Login from './utils/Auth/login';
import Logout from './utils/Auth/logout';
import Welcome from './pages/FirstLogin/Welcome';
import SetupGuide from './pages/FirstLogin/SetupGuide';
import Students from './pages/Students/Students';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ExamAdd from './pages/Exams/ExamAdd';
import ExamList, { HomeworkList, WorksheetList } from './pages/Exams/ExamList';
import ExamBankPage from './school_panel/pages/exam_bank/ExamBankPage';
import ExamBankDetailPage from './school_panel/pages/exam_bank/ExamBankDetailPage';
import ExamQuestionAdd from './pages/Exams/ExamQuestionAdd';
import ExamDetail from './pages/Exams/ExamDetail';
import GenerateExam from './pages/Exams/GenerateExam';
import ExamSetDetail from './pages/Exams/ExamSetDetail';
import MixedExam from './pages/Exams/MixedExam';
import WorksheetEditorPage from './pages/Worksheets/WorksheetEditorPage';
import MarkingPage from './pages/Worksheets/MarkingPage';
import StudentRequests from './pages/Students/StudentRequests';
import ForgotPassword from './utils/Auth/ForgotPassword';
import ResetPassword from './utils/Auth/ResetPassword';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminUsers from './pages/Admin/Users';
import AdminSchools from './pages/Admin/Schools';
import AdminExams from './pages/Admin/Exams';
import AdminStudentsAll from './pages/Admin/StudentsAll';
import AccountSharing from './pages/Admin/AccountSharing';
import RagSetup from './pages/Admin/RagSetup';
import RagList from './pages/Admin/RagList';
import ChapterVideos from './pages/Admin/ChapterVideos';
import AdminNotification from './pages/Admin/Notification';
import SchoolAIUsage from './pages/Admin/SchoolAIUsage';
import ClassSetup from './pages/SchoolSetup/ClassSetup';
import ChapterSetup from './pages/SchoolSetup/ChapterSetup';
import SchoolDashboard from './pages/Dashboard/SchoolDashboard';
import ClassPerformancePage from './school_panel/pages/class_performance/ClassPerformancePage';
import ClassPerformanceWithSection from './school_panel/pages/class_performance/ClassPerformanceWithSection';
import RoleGate from './school_panel/components/RoleGate';
import StudentProfile360 from './school_panel/pages/StudentProfile360';
import SchoolTasksPage from './pages/SchoolTasks/SchoolTasksPage';
import AlertsInbox from './school_panel/pages/AlertsInbox';
import CasesList from './school_panel/pages/CasesList';
import CaseDetail from './school_panel/pages/CaseDetail';
import TeachersAssignmentsPage from './school_panel/pages/setup/TeachersAssignmentsPage';
import StaffPage from './school_panel/pages/setup/StaffPage';
import AlertSettingsPage from './school_panel/pages/settings/AlertSettingsPage';
import CoachActivityPage from './school_panel/pages/settings/CoachActivityPage';
import SectionsPage from './school_panel/pages/settings/SectionsPage';
import MyAiPage from './school_panel/pages/settings/MyAiPage';
import ParentDigestPage from './school_panel/pages/parents/ParentDigestPage';
import ExamReportPage from './school_panel/pages/exam_report/ExamReportPage';
import PaperExamsPage from './school_panel/pages/paper_exams/PaperExamsPage';
import NewPaperExamPage from './school_panel/pages/paper_exams/NewPaperExamPage';
import SheetsPage from './school_panel/pages/paper_exams/SheetsPage';
import Landing from './pages/Public/Landing';
import { Privacy, Terms } from './pages/Public/Legal';
import Tutorials from './pages/Public/Tutorials';
import AiLimitDialog from './school_panel/components/ai/AiLimitDialog';
import { WELCOME_PATH, mustChangePassword } from './utils/Auth/firstLogin';
import RecoverRole from './utils/Auth/RecoverRole';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      
    },
  },
})


function SuperOnly({ children }: { children: React.ReactNode }) {
  const { role } = useAuth();
  if (role !== 'superuser') {
    return <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">403 - Not authorized</div>;
  }
  return <>{children}</>;
}

function HomeIndex() {
  const { role } = useAuth();
  if (role === 'superuser') {
    return (<><PageTitle title="Admin Dashboard" /><AdminDashboard /></>);
  }
  if (['school', 'school_admin', 'college_university', 'admin', 'teacher', 'counselor'].includes(role || '')) {
    return <Navigate to="/dashboard" replace />;
  }
  // Signed in without a usable saved role: /login would bounce straight back here.
  return <RecoverRole />;
}

/** `/` is the public landing page when signed out and the app home when signed in. */
function RootGate() {
  const { token } = useAuth();
  if (!token) return <Landing />;
  if (mustChangePassword()) return <Navigate to={WELCOME_PATH} replace />;
  return <Outlet />;
}

function App() {

  // const [loading, setLoading] = useState<boolean>(true);
  // const { pathname } = useLocation();

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, [pathname]);

  // useEffect(() => {
  //   setTimeout(() => setLoading(false), 1000);
  // }, []);

  // return loading ? (
  //   <Loader />
  // ) : 
  return (
    // <AuthProvider>

    //     <Routes />


    // </AuthProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<RootGate />}>
            <Route element={<DefaultLayout />}>
              <Route index element={<HomeIndex />} />
            </Route>
          </Route>
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/tutorials" element={<Tutorials />} />
          <Route element={<ProtectedRoute />}>
            {/* First sign-in: replace a temporary password, then set up the school (no sidebar). */}
            <Route path="/welcome" element={<><PageTitle title="Welcome" /><Welcome /></>} />
            <Route element={<DefaultLayout />}>
              <Route
                path="/setup-guide"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin']}>
                    <PageTitle title="Set up your school" />
                    <SetupGuide />
                  </RoleGate>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <>
                    <PageTitle title="School Dashboard" />
                    <SchoolDashboard />
                  </>
                }
              />
              <Route
                path="/analytics"
                element={<Navigate to="/class-performance" replace />}
              />
              <Route
                path="/class-performance"
                element={
                  <>
                    <PageTitle title="Class Performance" />
                    <RoleGate allow={['school_admin', 'school', 'admin', 'counselor', 'teacher']}>
                      <ClassPerformancePage />
                    </RoleGate>
                  </>
                }
              />
              <Route
                path="/school/section/:id"
                element={
                  <>
                    <PageTitle title="Class Performance" />
                    <RoleGate allow={['school_admin', 'school', 'admin', 'counselor', 'teacher']}>
                      <ClassPerformanceWithSection />
                    </RoleGate>
                  </>
                }
              />
              <Route
                path="/students"
                element={
                  <>
                    <PageTitle title="Students" />
                    <Students />
                  </>
                }
              />
              {/* Admin routes for superuser */}
              <Route
                path="/admin"
                element={
                  <SuperOnly>
                    <PageTitle title="Admin Dashboard" />
                    <AdminDashboard />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Users" />
                    <AdminUsers />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/schools"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Schools" />
                    <AdminSchools />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/exams"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Exams" />
                    <AdminExams />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/students"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Students" />
                    <AdminStudentsAll />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/account-sharing"
                element={
                  <SuperOnly>
                    <PageTitle title="Account sharing" />
                    <AccountSharing />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/rag-setup"
                element={
                  <SuperOnly>
                    <PageTitle title="RAG Setup" />
                    <RagSetup />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/rag"
                element={
                  <SuperOnly>
                    <PageTitle title="RAG List" />
                    <RagList />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/chapter-videos"
                element={
                  <SuperOnly>
                    <PageTitle title="Chapter Videos" />
                    <ChapterVideos />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/notification"
                element={
                  <SuperOnly>
                    <PageTitle title="Push Notifications" />
                    <AdminNotification />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/school-ai-usage"
                element={
                  <SuperOnly>
                    <PageTitle title="School User Credit Usage" />
                    <SchoolAIUsage />
                  </SuperOnly>
                }
              />
              <Route
                path="/students/profile/:studId"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin', 'teacher', 'counselor']}>
                    <PageTitle title="Student Profile" />
                    <StudentProfile360 />
                  </RoleGate>
                }
              />
              <Route
                path="/students/requests"
                element={
                  <>
                    <PageTitle title="Student Requests" />
                    <StudentRequests />
                  </>
                }
              />
              <Route
                path="/exams/exam-list"
                element={
                  <>
                    <PageTitle title="Exams" />
                    <ExamList />
                  </>
                }
              />
              <Route
                path="/worksheets"
                element={
                  <>
                    <PageTitle title="Worksheets" />
                    <WorksheetList />
                  </>
                }
              />
              <Route
                path="/exam-bank"
                element={
                  <>
                    <PageTitle title="Exam Bank" />
                    <ExamBankPage />
                  </>
                }
              />
              <Route
                path="/exam-bank/:examId"
                element={
                  <>
                    <PageTitle title="Exam Bank" />
                    <ExamBankDetailPage />
                  </>
                }
              />
              <Route
                path="/homework"
                element={
                  <>
                    <PageTitle title="Homework" />
                    <HomeworkList />
                  </>
                }
              />
              <Route
                path="/exams/add-exam"
                element={
                  <>
                    <PageTitle title="Add Exam" />
                    <ExamAdd />
                  </>
                }
              />
              <Route
                path="/exams/mixed-exam"
                element={
                  <>
                    <PageTitle title="Mixed Exam" />
                    <MixedExam />
                  </>
                }
              />
              <Route
                path="/exams/add-exam/:examId"
                element={
                  <>
                    <PageTitle title="Add Exam Questions" />
                    <ExamQuestionAdd />
                  </>
                }
              />
              <Route
                path="/exams/generate"
                element={
                  <>
                    <PageTitle title="Generate Exam" />
                    <GenerateExam />
                  </>
                }
              />
              <Route
                path="/exams/generate/job/:jobId"
                element={
                  <>
                    <PageTitle title="Generate Exam" />
                    <GenerateExam />
                  </>
                }
              />
              <Route
                path="/exams/paper"
                element={
                  <>
                    <PageTitle title="Paper Exams" />
                    <PaperExamsPage />
                  </>
                }
              />
              <Route
                path="/exams/paper/new"
                element={
                  <>
                    <PageTitle title="New Paper Exam" />
                    <NewPaperExamPage />
                  </>
                }
              />
              <Route
                path="/exams/paper/:examId/sheets"
                element={
                  <>
                    <PageTitle title="Scan Sheets" />
                    <SheetsPage />
                  </>
                }
              />
              <Route
                path="/worksheets/:examId/edit"
                element={
                  <>
                    <PageTitle title="Worksheet Editor" />
                    <WorksheetEditorPage />
                  </>
                }
              />
              <Route
                path="/worksheets/:examId/marking"
                element={
                  <>
                    <PageTitle title="Marking" />
                    <MarkingPage />
                  </>
                }
              />
              <Route
                path="/exams/exam-detail/:examId"
                element={
                  <>
                    <PageTitle title="Exam Detail" />
                    <ExamDetail />
                  </>
                }
              />
              <Route
                path="/exams/exam-set/:examSetId"
                element={
                  <>
                    <PageTitle title="Exam Set" />
                    <ExamSetDetail />
                  </>
                }
              />
              <Route
                path="/exams/report/:examId"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin', 'teacher', 'counselor']}>
                    <PageTitle title="Exam Report" />
                    <ExamReportPage />
                  </RoleGate>
                }
              />
              <Route path="/logout" element={<Logout />} />

              {/* School Setup Routes */}
              <Route
                path="/school-setup/class"
                element={
                  <>
                    <PageTitle title="Class Setup" />
                    <ClassSetup />
                  </>
                }
              />
              <Route
                path="/school-setup/chapters"
                element={
                  <>
                    <PageTitle title="Chapter Setup" />
                    <ChapterSetup />
                  </>
                }
              />
              <Route
                path="/school-setup/staff"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin']}>
                    <PageTitle title="Staff" />
                    <StaffPage />
                  </RoleGate>
                }
              />
              <Route
                path="/school-setup/teachers"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin']}>
                    <PageTitle title="Teachers" />
                    <TeachersAssignmentsPage />
                  </RoleGate>
                }
              />
              <Route
                path="/school-tasks"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin', 'teacher']}>
                    <PageTitle title="School tasks" />
                    <SchoolTasksPage />
                  </RoleGate>
                }
              />
              <Route
                path="/alerts"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin', 'teacher', 'counselor']}>
                    <PageTitle title="Alerts" />
                    <AlertsInbox />
                  </RoleGate>
                }
              />
              <Route
                path="/cases"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin', 'teacher', 'counselor']}>
                    <PageTitle title="Cases" />
                    <CasesList />
                  </RoleGate>
                }
              />
              <Route
                path="/cases/:caseId"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin', 'teacher', 'counselor']}>
                    <PageTitle title="Case Detail" />
                    <CaseDetail />
                  </RoleGate>
                }
              />
              <Route
                path="/settings/alerts"
                element={
                  <>
                    <PageTitle title="Alert rules" />
                    <RoleGate allow={['school_admin', 'school', 'admin']}>
                      <AlertSettingsPage />
                    </RoleGate>
                  </>
                }
              />
              <Route
                path="/settings/coach-activity"
                element={
                  <>
                    <PageTitle title="Coach activity" />
                    <RoleGate allow={['school_admin', 'school', 'admin']}>
                      <CoachActivityPage />
                    </RoleGate>
                  </>
                }
              />
              <Route
                path="/settings/ai"
                element={
                  <RoleGate allow={['school_admin', 'school', 'admin', 'teacher', 'counselor', 'staff']}>
                    <PageTitle title="My AI" />
                    <MyAiPage />
                  </RoleGate>
                }
              />
              <Route
                path="/settings/sections"
                element={
                  <>
                    <PageTitle title="Sections" />
                    <RoleGate allow={['school_admin', 'school', 'admin']}>
                      <SectionsPage />
                    </RoleGate>
                  </>
                }
              />
              <Route
                path="/parents/digest"
                element={
                  <>
                    <PageTitle title="Parent Digest" />
                    <RoleGate allow={['school_admin', 'school', 'admin']}>
                      <ParentDigestPage />
                    </RoleGate>
                  </>
                }
              />
            </Route>
          </Route>
          <Route element={<NonAuthRoute />}>
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<><PageTitle title="Sign in" /><Login /></>} />
              <Route
                path="/auth/forgot-password"
                element={
                  <>
                    <PageTitle title="Forgot Password" />
                    <ForgotPassword />
                  </>
                }
              />
              <Route
                path="/auth/reset-password"
                element={
                  <>
                    <PageTitle title="Reset Password" />
                    <ResetPassword />
                  </>
                }
              />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
      <AiLimitDialog />
      <ReactQueryDevtools />
    </QueryClientProvider>

  );
}

export default App;
